import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Fade,
  Skeleton,
  ThemeProvider,
  createTheme,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InboxIcon from "@mui/icons-material/Inbox";
import { toast } from "react-toastify";
import { schedulingApi } from "../Components/schedulingApi";
import listStyles from "./AllEnquiries.module.scss";
import { useTheme } from "../hooks/useTheme";
import { createMuiThemeFromMode } from "../theme/theme";

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: { styleOverrides: { root: { boxShadow: "none" } } },
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
  },
};

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

function formatDt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function statusChipProps(status) {
  const s = (status || "").toLowerCase();
  if (s === "pending") return { color: "warning", label: status || "pending" };
  if (s === "approved") return { color: "success", label: status || "approved" };
  if (s === "rejected") return { color: "error", label: status || "rejected" };
  return { color: "default", label: status || "—" };
}

export default function RescheduleRequestsPage() {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await schedulingApi.listRescheduleRequests(
        filter === "all" ? {} : { status: "pending" }
      );
      if (data?.error) {
        toast.error(data.error);
        setRequests([]);
        return;
      }
      setRequests(data.requests || []);
    } catch (e) {
      toast.error(e?.message || "Failed to load reschedule requests");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApprove = async (requestId) => {
    setActingId(requestId);
    try {
      const data = await schedulingApi.approveRescheduleRequest(requestId);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success(data.message || "Reschedule approved.");
      load();
    } catch (e) {
      toast.error(e?.message || "Failed to approve");
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (requestId) => {
    if (!window.confirm("Reject this reschedule request?")) return;
    setActingId(requestId);
    try {
      const data = await schedulingApi.rejectRescheduleRequest(requestId);
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      toast.success(data.message || "Request rejected.");
      load();
    } catch (e) {
      toast.error(e?.message || "Failed to reject");
    } finally {
      setActingId(null);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={listStyles.root}>
        <Paper elevation={0} className={listStyles.contentPaper}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/schedule")}
            sx={{ mb: 2 }}
            size="small"
            color="inherit"
          >
            Back to schedule
          </Button>

          <Box sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="h4" className={listStyles.pageTitle}>
                  Reschedule requests
                </Typography>
                <Typography variant="body2" className={listStyles.pageSubtitle} sx={{ mt: 0.5 }}>
                  Approve or reject session reschedule requests from lecturers and other users.
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Fade in>
            <FilterPaper variant="outlined">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Show requests
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={filter}
                onChange={(_, v) => v != null && setFilter(v)}
                aria-label="request filter"
              >
                <ToggleButton value="pending">Pending</ToggleButton>
                <ToggleButton value="all">All</ToggleButton>
              </ToggleButtonGroup>
            </FilterPaper>
          </Fade>

          {loading ? (
            <Box className={listStyles.loadingContainer}>
              <Box sx={{ width: "100%" }}>
                <Skeleton variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
              </Box>
            </Box>
          ) : requests.length === 0 ? (
            <Box className={listStyles.emptyState}>
              <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No reschedule requests
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filter === "pending"
                  ? "There are no pending requests right now."
                  : "No requests have been recorded yet."}
              </Typography>
            </Box>
          ) : (
            <Paper variant="outlined" className={listStyles.tableContainer}>
              <Table size="small" stickyHeader className={listStyles.enquiriesTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Session / Event</TableCell>
                    <TableCell>Current time</TableCell>
                    <TableCell>Requested time</TableCell>
                    <TableCell>Requested by</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((r) => {
                    const chip = statusChipProps(r.status);
                    return (
                      <TableRow key={r.id} hover className={listStyles.tableRow}>
                        <TableCell>
                          <Typography variant="body2" className={listStyles.primaryText}>
                            {r.event_title || `Event #${r.event_id}`}
                          </Typography>
                          {r.event_id && (
                            <Button
                              size="small"
                              variant="text"
                              sx={{ mt: 0.5, p: 0, minWidth: 0 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/schedule/events/${r.event_id}`);
                              }}
                            >
                              View session
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" className={listStyles.secondaryText}>
                            {formatDt(r.current_start)} → {formatDt(r.current_end)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" className={listStyles.secondaryText}>
                            {formatDt(r.requested_start_datetime)} → {formatDt(r.requested_end_datetime)}
                          </Typography>
                        </TableCell>
                        <TableCell>{r.requested_by_name || "—"}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={chip.color}
                            label={String(chip.label)}
                            sx={{ textTransform: "capitalize" }}
                          />
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          {r.status === "pending" && (
                            <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleOutlineIcon />}
                                onClick={() => handleApprove(r.id)}
                                disabled={actingId === r.id}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={<CancelOutlinedIcon />}
                                onClick={() => handleReject(r.id)}
                                disabled={actingId === r.id}
                              >
                                Reject
                              </Button>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
